import {ingredientDensity,recipeBreakdown} from './recipe-breakdown.mjs';
import {validateRecipe} from './compost-plan.mjs';

export const YARD_ROLES = ['cooking', 'staging', 'curing'];
export const STAGING_MAP_COLOR = '#c8902a';
const fail = (message, status = 400) => Object.assign(new Error(message), { status });
const text = (value, max, required = false) => {
  if (value == null || value === '') {
    if (required) throw fail('A name and identification are required.');
    return '';
  }
  if (typeof value !== 'string' || value.length > max) throw fail('Check the name, location and notes.');
  return value.trim();
};
const quantity = (value, label) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0 || n > 1e9) throw fail(`${label} must be a positive number.`);
  return n;
};
const add = (a, b) => (b == null ? a : a == null ? b : a + b);
const sub = (a, b) => (a == null ? null : a - (b || 0));

export function normalizeYardRole(value, fallback = 'cooking') {
  const role = value == null || value === '' ? fallback : String(value).trim();
  if (!YARD_ROLES.includes(role)) throw fail('Choose staging, cooking, or curing.');
  return role;
}

export function yardRoleLabel(role) {
  return { cooking: 'Cooking', staging: 'Staging', curing: 'Curing' }[role] || 'Cooking';
}

export function yardRoleColor(role, fallback) {
  return role === 'staging' ? STAGING_MAP_COLOR : fallback;
}

export function remainingStaging(additions = [], transfers = []) {
  const inboundVolume = additions.reduce((sum, row) => add(sum, row.cubic_yards ?? row.cubicYards ?? null), null);
  const inboundTons = additions.reduce((sum, row) => add(sum, row.weight ?? null), null);
  const pulledVolume = transfers.reduce((sum, row) => add(sum, row.volume ?? null), null);
  const pulledTons = transfers.reduce((sum, row) => add(sum, row.tons ?? null), null);
  return {
    inboundVolume,
    inboundTons,
    pulledVolume,
    pulledTons,
    remainingVolume: sub(inboundVolume, pulledVolume),
    remainingTons: sub(inboundTons, pulledTons),
  };
}

export function describeRecipe(snapshot) {
  if (!snapshot?.lines?.length) return null;
  return `${snapshot.name}: ${snapshot.lines.map((line) => `${line.name} ${line.percent}%`).join(' · ')} (by ${snapshot.basis})`;
}

function snapshotRecipe(recipe, catalog) {
  validateRecipe(recipe, true);
  const lines = recipe.lines.map((line) => {
    const ingredient = catalog.find((item) => item.id === line.ingredientId && item.is_active !== false);
    if (!ingredient) throw fail('An ingredient is no longer available. Refresh the catalog.');
    return {
      ingredientId: line.ingredientId,
      percent: line.percent,
      name: ingredient.name,
      densityLbsPerCuft: ingredientDensity(ingredient.density_lbs_per_cuft ?? ingredient.densityLbsPerCuft),
    };
  });
  return {
    name: text(recipe.name || 'Batch recipe', 120, true),
    basis: recipe.basis,
    lines,
    templateId: recipe.templateId ?? null,
    templateVersion: recipe.templateVersion ?? null,
  };
}

export function previewMix(recipeSnapshot, mix) {
  return recipeBreakdown(recipeSnapshot, { volume: mix.volume, tons: mix.tons });
}

export function normalizeMixRequest(body, catalog = []) {
  if (typeof body?.actionId !== 'string' || !/^[a-zA-Z0-9_-]{8,100}$/.test(body.actionId)) {
    throw fail('A valid mix request key is required.');
  }
  const volume = quantity(body.mix?.volume ?? body.volume, 'Mix volume');
  const tons = quantity(body.mix?.tons ?? body.tons, 'Mix weight');
  if (volume == null && tons == null) throw fail('Enter the mix volume or weight being pulled onto a cooking pad.');
  const recipe = body.recipeSnapshot || body.recipe;
  if (!recipe) throw fail('Choose a recipe before mixing onto a cooking pad.');
  const recipeSnapshot = snapshotRecipe(recipe, catalog);
  if (recipeSnapshot.basis === 'volume' && volume == null) throw fail('This recipe is by volume. Enter cubic yards to mix.');
  if (recipeSnapshot.basis === 'mass' && tons == null) throw fail('This recipe is by mass. Enter US tons to mix.');
  const breakdown = previewMix(recipeSnapshot, { volume, tons });
  if (breakdown.anchor == null) throw fail('Record the mix quantity for the recipe basis.');
  const destination = body.destination || {};
  const mode = destination.mode === 'new' || destination.mode === 'existing' ? destination.mode : (destination.windrowId ? 'existing' : 'new');
  let dest = { mode, shape: destination.shape === 'pile' ? 'pile' : 'line' };
  if (mode === 'existing') {
    const windrowId = Number(destination.windrowId ?? destination.id);
    if (!Number.isSafeInteger(windrowId) || windrowId < 1) throw fail('Choose a cooking windrow or pad.');
    dest.windrowId = windrowId;
  } else {
    dest = {
      ...dest,
      name: text(destination.name, 160, true),
      rowId: text(destination.rowId, 80, true),
      location: text(destination.location, 200),
      notes: text(destination.notes, 2000),
    };
  }
  const remaining = remainingStaging(body.sourceAdditions || [], body.sourceTransfers || []);
  if (volume != null && remaining.remainingVolume != null && volume > remaining.remainingVolume + 1e-6) {
    throw fail('That mix volume is more than the remaining staging estimate.');
  }
  if (tons != null && remaining.remainingTons != null && tons > remaining.remainingTons + 1e-6) {
    throw fail('That mix weight is more than the remaining staging estimate.');
  }
  return {
    actionId: body.actionId,
    recipeSnapshot,
    mix: { volume, tons },
    destination: dest,
    notes: text(body.notes, 2000),
    lines: breakdown.rows.map((row) => ({
      ingredientId: row.ingredientId,
      name: row.name,
      percent: row.percent,
      densityLbsPerCuft: row.density,
      cubicYards: row.volume,
      weight: row.tons,
    })),
    remaining,
    recipeText: describeRecipe(recipeSnapshot),
  };
}
